import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import type { AuthCustomer } from '@getjust/api-schema';

import { useCustomRouter, usePrelude } from '$hooks/client';
import { useUpdateEmail } from '$hooks/mutations';
import { useFormMessageAtom, useLoginAtom, useTemporaryCartKeyAtom } from '$hooks/state';
import { MUTATION_LOGIN_KEY } from '$src/constants';
import { useCart } from '$src/hooks/queries';
import { useShowChallengeAtom } from '$src/hooks/state/useShowChallengeAtom';
import { authAPI } from '$src/http';

export interface LoginParams {
  email: string;
  /** Optional preferred channel to use for OTP delivery. When resending, pass the previous channel to maintain consistency. */
  preferredChannel?: AuthCustomer.PreferredChannel;
}

export const useLogin = () => {
  const { mutateAsync: updateEmail } = useUpdateEmail();
  const { onLoginChange, loginInfo } = useLoginAtom();
  const router = useCustomRouter();
  const { showLoginChallenge } = useShowChallengeAtom();
  const { formMessage } = useFormMessageAtom();
  const { data: cart } = useCart();
  const { onCartKeyChange } = useTemporaryCartKeyAtom();

  return useMutation({
    mutationFn: (params: LoginParams | string) => {
      // Support both old string API and new object API for backwards compatibility
      const email = typeof params === 'string' ? params : params.email;
      const preferredChannel = typeof params === 'string' ? loginInfo?.channel : params.preferredChannel;

      const cartKey = uuidv4();
      onCartKeyChange(cartKey);

      const payload: AuthCustomer.AuthLoginRequestBody = {
        email,
        clientId: formMessage?.shopId ?? 'web',
        cartkey: cartKey,
        ...(cart?.id ? { cart: cart.id } : {}),
        // Include Prelude dispatchId if available for silent verification
        // TODO: Uncomment this when it's activated on prelude
        ///...(isPreludeEnabled && dispatchId ? { dispatchId } : {}),
        // Preserve preferred channel when resending
        ...(preferredChannel ? { preferredChannel } : {}),
      };

      return authAPI.post<AuthCustomer.AuthLoginResponseBody>('/login', payload);
    },
    mutationKey: [MUTATION_LOGIN_KEY],
    onError: async (error: any, params: LoginParams | string) => {
      const email = typeof params === 'string' ? params : params.email;

      if (!router.pathname.includes('/unauthenticated')) {
        router.push('/unauthenticated');
      }

      if (router.pathname.includes('/unauthenticated')) {
        await updateEmail({
          email,
        });
      }
    },
    onSuccess: ({ data }) => {
      onLoginChange(data);
      showLoginChallenge();
    },
  });
};
